import * as LocalAuthentication from 'expo-local-authentication';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

export type BiometricType = 'face' | 'fingerprint' | 'iris' | null;

// Normalized enrolled-level string. Mirrors expo's SecurityLevel enum but as a
// string so it survives serialization (analytics events, remote logging) and
// stays stable if expo renumbers.
export type EnrolledLevel = 'none' | 'secret' | 'weak' | 'strong';

export type BiometricCapabilities = {
  loading: boolean;
  // Hardware present.
  available: boolean;
  // User has enrolled a biometric at the OS level (any class).
  enrolled: boolean;
  // Highest OS-level enrollment class currently registered on the device.
  // Callers can key analytics off this to distinguish e.g. "weak face only"
  // from "strong fingerprint or Class 3 face" without re-querying expo.
  enrolledLevel: EnrolledLevel;
  // A prompt on this device is expected to complete successfully with the
  // current enrollment mix. See the Android Class 1/2/3 taxonomy note in
  // isUsable() for why this differs from `enrolled`. `usable === true` implies
  // `enrolled === true` directly, which in turn implies `available === true`
  // (the OS can't enroll biometric on hardware-absent devices, so
  // isEnrolledAsync returns false there), so callers can gate on `usable`
  // alone.
  usable: boolean;
  type: BiometricType;
};

const INITIAL_NATIVE: BiometricCapabilities = {
  loading: true,
  available: false,
  enrolled: false,
  enrolledLevel: 'none',
  usable: false,
  type: null,
};

const WEB: BiometricCapabilities = {
  loading: false,
  available: false,
  enrolled: false,
  enrolledLevel: 'none',
  usable: false,
  type: null,
};

const pickType = (
  types: LocalAuthentication.AuthenticationType[]
): BiometricType => {
  if (types.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) return 'face';
  if (types.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) return 'fingerprint';
  if (types.includes(LocalAuthentication.AuthenticationType.IRIS)) return 'iris';
  return null;
};

const normalizeLevel = (level: LocalAuthentication.SecurityLevel): EnrolledLevel => {
  switch (level) {
    case LocalAuthentication.SecurityLevel.BIOMETRIC_STRONG:
      return 'strong';
    case LocalAuthentication.SecurityLevel.BIOMETRIC_WEAK:
      return 'weak';
    case LocalAuthentication.SecurityLevel.SECRET:
      return 'secret';
    default:
      return 'none';
  }
};

// Android quirk: BiometricPrompt only foregrounds Class 3 (STRONG) biometrics —
// fingerprint or Class 3 face (Pixel 8+). Class 2 face (Pixel 7 and earlier)
// runs as a background/passive check that never becomes the prompt UI, so if
// STRONG isn't enrolled the prompt has no foreground path to success. The
// symptom is easiest to see on devices with fingerprint hardware — the prompt
// asks for a finger the user hasn't registered — but the same trap applies to
// face-only devices where the only face is Class 2. Guarding on `strong` here
// catches both cases with a single check. iOS has no such split (Face ID and
// Touch ID are both effectively Class 3) so we always report usable there.
const isUsable = (enrolledLevel: EnrolledLevel): boolean => {
  if (Platform.OS !== 'android') return true;
  return enrolledLevel === 'strong';
};

export const useBiometricCapabilities = (): BiometricCapabilities => {
  const [state, setState] = useState<BiometricCapabilities>(
    Platform.OS === 'web' ? WEB : INITIAL_NATIVE
  );

  useEffect(() => {
    if (Platform.OS === 'web') return;
    let cancelled = false;
    (async () => {
      try {
        const [available, enrolled, types, rawLevel] = await Promise.all([
          LocalAuthentication.hasHardwareAsync(),
          LocalAuthentication.isEnrolledAsync(),
          LocalAuthentication.supportedAuthenticationTypesAsync(),
          LocalAuthentication.getEnrolledLevelAsync(),
        ]);
        if (cancelled) return;
        const enrolledLevel = normalizeLevel(rawLevel);
        setState({
          loading: false,
          available,
          enrolled,
          enrolledLevel,
          usable: enrolled && isUsable(enrolledLevel),
          type: pickType(types),
        });
      } catch {
        if (!cancelled) {
          setState({
            loading: false,
            available: false,
            enrolled: false,
            enrolledLevel: 'none',
            usable: false,
            type: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
